import React, { useState, useEffect } from 'react';
import { DataTable } from '@/components/DataTable';
import { labTestsService } from '@/services/labTests';
import { getApiErrorMessage } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus } from '@phosphor-icons/react';
import { formatDate } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

export default function LabPage() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [tests, setTests] = useState([]);
  const [newTest, setNewTest] = useState({
    testName: '',
    patient: '',
    date: '',
  });

  useEffect(() => {
    (async () => {
      try {
        const r = await labTestsService.getAll();
        setTests(r.data);
      } catch (error) {
        toast.error(getApiErrorMessage(error));
      }
    })();
  }, []);

  const columns = [
    { key: 'testName', header: 'Test', sortable: true },
    { key: 'patient', header: 'Patient', sortable: true },
    {
      key: 'date',
      header: 'Date',
      sortable: true,
      render: (value) => formatDate(value),
    },
    {
      key: 'status',
      header: 'Status',
      render: (value) => {
        const variants = {
          Completed: 'default',
          'In Progress': 'secondary',
          Pending: 'outline',
        };
        return <Badge variant={variants[value]}>{value}</Badge>;
      },
    },
  ];

  return (
    <div className="space-y-6" data-testid="lab-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-light tracking-tighter mb-2">Laboratory</h1>
          <p className="text-sm md:text-base leading-relaxed text-muted-foreground">
            Manage lab tests and reports
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-lab-test-button">
              <Plus className="mr-2 h-4 w-4" weight="bold" />
              New Test Order
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>New Test Order</DialogTitle>
              <DialogDescription>
                Create a new lab test order
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="test-patient">Patient Name</Label>
                <Input
                  id="test-patient"
                  placeholder="John Smith"
                  value={newTest.patient}
                  onChange={(e) => setNewTest((t) => ({ ...t, patient: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="test-name">Test Name</Label>
                <Input
                  id="test-name"
                  placeholder="Complete Blood Count"
                  value={newTest.testName}
                  onChange={(e) => setNewTest((t) => ({ ...t, testName: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="test-date">Test Date</Label>
                <Input
                  id="test-date"
                  type="date"
                  value={newTest.date}
                  onChange={(e) => setNewTest((t) => ({ ...t, date: e.target.value }))}
                />
              </div>
              <Button
                className="w-full"
                onClick={async () => {
                  try {
                    await labTestsService.create({
                      testName: newTest.testName,
                      patient: newTest.patient,
                      date: newTest.date,
                    });
                    toast.success('Test order created successfully');
                    setIsDialogOpen(false);
                    setNewTest({ testName: '', patient: '', date: '' });
                    const r = await labTestsService.getAll();
                    setTests(r.data);
                  } catch (error) {
                    toast.error(getApiErrorMessage(error));
                  }
                }}
              >
                Create Order
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <DataTable data={tests} columns={columns} searchPlaceholder="Search tests..." />
    </div>
  );
}